/* LPOS-CARD-STOCK.JS
   - Opent voorraad-modal vanaf knoppen met [data-lpos-open]
   - Haalt winkels uit registry + stock per winkel via EAN
   - Voorkomt ghost click / dubbele open op mobiel
   - Opent NIET tijdens scrollen op mobiel
   - GA4 tracking (gtag):
       1) lpos_open_stock  (wanneer modal opent)
       2) lpos_store_click (wanneer op winkel-link wordt geklikt)
   - Verbetering:
       * stuurt GEEN unknown store_name meer
       * store click event wordt alleen verstuurd als er echt een winkelnaam is
*/

(function () {
  'use strict';

  const CONFIG = {
    STORE_REGISTRY_URL: 'https://app.lpos.nl/api/stores/registry.json',
    API_TIMEOUT: 5000,

    // Zet tijdelijk op true als je DebugView extra wil forceren
    GA_DEBUG: false,

    // Timing guards
    OPEN_LOCK_MS: 550,
    GHOST_CLICK_MS: 650,
    MODAL_CLOSE_GUARD_MS: 450,

    // Mobiel: max beweging om nog als "tap" te tellen
    TAP_MOVE_THRESHOLD: 10
  };

  const modal = {
    el: null,
    body: null,
    subtitle: null
  };

  let stores = [];
  let modalOpenedAt = 0;
  let lastTouchOpenAt = 0;
  let openLockUntil = 0;

  let touchStartX = 0;
  let touchStartY = 0;

  // Context voor tracking
  let currentContext = {
    ean: '',
    title: '',
    isPreOrder: false
  };

  function gaEvent(name, params) {
    if (typeof window.gtag !== 'function') return;

    const payload = Object.assign({}, params || {});
    if (CONFIG.GA_DEBUG) payload.debug_mode = true;

    window.gtag('event', name, payload);
  }

  function formatPrice(price) {
    if (price === null || price === undefined || price === '' || isNaN(price)) {
      return '-';
    }

    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  }

  async function fetchJson(url, timeoutMs) {
    const controller = new AbortController();
    const timeout = setTimeout(function () {
      controller.abort();
    }, timeoutMs);

    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return await res.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  async function fetchStoreRegistry() {
    try {
      const data = await fetchJson(CONFIG.STORE_REGISTRY_URL, CONFIG.API_TIMEOUT);
      return data && data.stores ? data.stores : [];
    } catch (e) {
      return [
        { subdomain: 'leiden', displayName: 'Leiden' },
        { subdomain: 'dordrecht', displayName: 'Dordrecht' },
        { subdomain: 'delft', displayName: 'Delft' }
      ];
    }
  }

  async function fetchStoreStock(store, ean) {
    const url = 'https://' + store.subdomain + '.lpos.nl/api/products/eaninformation/' + encodeURIComponent(ean) + '.json';

    try {
      const data = await fetchJson(url, CONFIG.API_TIMEOUT);

      if (!data || !data.success || !data.found) {
        return {
          storeName: store.displayName || '',
          stock: 0,
          available: false,
          price: null,
          productUrl: 'https://' + store.subdomain + '.lpos.nl'
        };
      }

      return {
        storeName: store.displayName || data.storeName || '',
        stock: Math.max(0, parseInt(data.stock, 10) || 0),
        available: !!data.available,
        price: data.price ? parseFloat(data.price) : null,
        productUrl: data.productUrl || ('https://' + store.subdomain + '.lpos.nl')
      };
    } catch (e) {
      return {
        storeName: store.displayName || '',
        stock: 0,
        available: false,
        price: null,
        productUrl: 'https://' + store.subdomain + '.lpos.nl'
      };
    }
  }

  function getStockClass(isPreOrder, store) {
    if (isPreOrder) return 'lpos-prerelease';
    if (store.available && store.stock > 0) return 'lpos-in-stock';
    return 'lpos-out-of-stock';
  }

  function getStockText(isPreOrder, store) {
    if (isPreOrder) return 'Binnenkort beschikbaar';
    if (store.available && store.stock > 0) return 'Op voorraad';
    return 'Geen voorraad';
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function renderTable(isPreOrder, storeData, ctx) {
    const ean = ctx && ctx.ean ? ctx.ean : '';
    const title = ctx && ctx.title ? ctx.title : '';

    storeData.sort(function (a, b) {
      const aInStock = a.available && a.stock > 0;
      const bInStock = b.available && b.stock > 0;

      if (aInStock !== bInStock) return bInStock - aInStock;
      return (a.storeName || '').localeCompare(b.storeName || '');
    });

    let html = `
      <table class="lpos-stores-table">
        <thead>
          <tr>
            <th>Winkel</th>
            <th>Voorraad</th>
            <th>Prijs</th>
          </tr>
        </thead>
        <tbody>
    `;

    storeData.forEach(function (store) {
      const inStock = store.available && store.stock > 0;

      const safeStoreName = escapeHtml(store.storeName);
      const safeTitle = escapeHtml(title);
      const safeEan = escapeHtml(ean);

      const hasStoreName = !!safeStoreName;

      const nameCell = (inStock && hasStoreName)
        ? `<a href="${store.productUrl}"
             target="_blank"
             rel="noopener"
             class="lpos-store-link"
             data-store="${safeStoreName}"
             data-ean="${safeEan}"
             data-title="${safeTitle}">
             ${safeStoreName}
           </a>`
        : (safeStoreName || 'Onbekende winkel');

      const priceCell = (inStock && !isPreOrder) ? formatPrice(store.price) : '';

      html += `
        <tr>
          <td>${nameCell}</td>
          <td class="${getStockClass(isPreOrder, store)}">
            ${getStockText(isPreOrder, store)}
          </td>
          <td>${priceCell}</td>
        </tr>
      `;
    });

    html += `
        </tbody>
      </table>
    `;

    return html;
  }

  function openModal(titleText) {
    modalOpenedAt = Date.now();

    modal.el.classList.add('is-open');
    modal.el.setAttribute('aria-hidden', 'false');

    document.documentElement.style.overflow = 'hidden';

    modal.subtitle.textContent = titleText || '';
    modal.body.innerHTML = '<div class="lpos-loading">Voorraad ophalen…</div>';
  }

  function closeModal() {
    modal.el.classList.remove('is-open');
    modal.el.setAttribute('aria-hidden', 'true');

    document.documentElement.style.overflow = '';
  }

  function bindModalClose() {
    modal.el.addEventListener('click', function (e) {
      if (Date.now() - modalOpenedAt < CONFIG.MODAL_CLOSE_GUARD_MS) return;

      if (e.target && e.target.matches('[data-lpos-close]')) {
        e.preventDefault();
        e.stopPropagation();
        closeModal();
      }
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.el.classList.contains('is-open')) {
        closeModal();
      }
    });
  }

  function bindStoreClickTracking() {
    document.addEventListener('click', function (e) {
      const link = e.target && e.target.closest ? e.target.closest('.lpos-store-link') : null;
      if (!link) return;

      const store = (link.dataset.store || '').trim();
      const ean = (link.dataset.ean || currentContext.ean || '').trim();
      const title = (link.dataset.title || currentContext.title || '').trim();

      // Belangrijk: geen event sturen zonder echte winkelnaam
      if (!store) return;

      gaEvent('lpos_store_click', {
        event_category: 'LPOS',
        event_label: store,
        store_name: store,
        ean: ean,
        item_name: title
      });
    }, true);
  }

  function isTouchLikeDevice() {
    return !!(
      window.matchMedia &&
      window.matchMedia('(hover: none) and (pointer: coarse)').matches
    );
  }

  function getBtnFromEvent(e) {
    if (!e || !e.target || !e.target.closest) return null;
    return e.target.closest('[data-lpos-open]');
  }

  function stopAll(e) {
    if (!e) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
  }

  async function handleOpen(e) {
    const btn = getBtnFromEvent(e);
    if (!btn) return;

    if (e.type === 'click' && Date.now() - lastTouchOpenAt < CONFIG.GHOST_CLICK_MS) {
      return;
    }

    const now = Date.now();
    if (now < openLockUntil) return;
    openLockUntil = now + CONFIG.OPEN_LOCK_MS;

    stopAll(e);

    const ean = (btn.getAttribute('data-ean') || '').trim();
    if (!ean) return;

    const prereleaseVal = (btn.getAttribute('data-prerelease') || '').toLowerCase();
    const isPreOrder =
      prereleaseVal === 'prerelease' ||
      prereleaseVal === 'pre' ||
      prereleaseVal === 'true';

    const title = btn.getAttribute('data-title') || '';

    currentContext = {
      ean: ean,
      title: title,
      isPreOrder: isPreOrder
    };

    openModal(title);

    gaEvent('lpos_open_stock', {
      event_category: 'LPOS',
      event_label: title || ean,
      ean: ean,
      item_name: title,
      is_preorder: isPreOrder ? 1 : 0
    });

    if (!stores.length) {
      modal.body.innerHTML = '<div class="lpos-loading">Geen winkels gevonden.</div>';
      return;
    }

    const results = await Promise.allSettled(
      stores.map(function (store) {
        return fetchStoreStock(store, ean);
      })
    );

    const storeData = results
      .filter(function (result) {
        return result.status === 'fulfilled' && result.value;
      })
      .map(function (result) {
        return result.value;
      });

    if (!storeData.length) {
      modal.body.innerHTML = '<div class="lpos-loading">Geen voorraadinfo beschikbaar.</div>';
      return;
    }

    modal.body.innerHTML = renderTable(isPreOrder, storeData, currentContext);
  }

  function bindOpenEvents() {
    const touchLike = isTouchLikeDevice();

    if (touchLike) {
      document.addEventListener('touchstart', function (e) {
        const btn = getBtnFromEvent(e);
        if (!btn || !e.touches || !e.touches.length) return;

        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true, capture: true });

      document.addEventListener('touchend', function (e) {
        const btn = getBtnFromEvent(e);
        if (!btn || !e.changedTouches || !e.changedTouches.length) return;

        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;

        const dx = Math.abs(endX - touchStartX);
        const dy = Math.abs(endY - touchStartY);

        if (dx > CONFIG.TAP_MOVE_THRESHOLD || dy > CONFIG.TAP_MOVE_THRESHOLD) {
          return;
        }

        lastTouchOpenAt = Date.now();
        handleOpen(e);
      }, { passive: false, capture: true });
    } else {
      document.addEventListener('click', handleOpen, true);
    }
  }

  async function init() {
    modal.el = document.getElementById('lpos-modal');
    modal.body = document.getElementById('lpos-modal-body');
    modal.subtitle = document.getElementById('lpos-modal-subtitle');

    if (!modal.el || !modal.body || !modal.subtitle) return;

    bindModalClose();
    bindStoreClickTracking();

    stores = await fetchStoreRegistry();

    bindOpenEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();